import { supabase } from '@/lib/supabase';
import { Stocktake, PaginatedResult } from '@/types/models';
import { PAGE_SIZE } from '@/lib/constants';

export interface FetchStocktakesParams {
  search?: string;
  status?: string;
  page?: number;
}

export async function fetchStocktakes(
  params: FetchStocktakesParams = {}
): Promise<PaginatedResult<Stocktake>> {
  const { search = '', status = '', page = 0 } = params;
  const from = page * PAGE_SIZE;
  const to = from + PAGE_SIZE - 1;

  let query = supabase
    .from('stocktakes')
    .select('*, creator:profiles!stocktakes_created_by_fkey(full_name), completer:profiles!stocktakes_completed_by_fkey(full_name)', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range(from, to);

  if (search.trim()) {
    query = query.or(
      `stocktake_number.ilike.%${search.trim()}%,title.ilike.%${search.trim()}%,notes.ilike.%${search.trim()}%`
    );
  }

  if (status) query = query.eq('status', status);

  const { data, error, count } = await query;
  if (error) throw error;

  return { data: (data ?? []) as Stocktake[], count: count ?? 0 };
}

export async function fetchStocktakeById(id: string): Promise<Stocktake> {
  const { data, error } = await supabase
    .from('stocktakes')
    .select('*, items:stocktake_items(*, laptop:laptops(id, stock_id, brand, model, serial_number, status), accessory:accessories(id, sku, name, category, quantity))')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as Stocktake;
}

export async function createStocktakeRpc(payload: {
  title: string;
  item_type?: 'all' | 'laptops' | 'accessories';
  notes?: string | null;
}): Promise<{
  success: boolean;
  stocktake_id: string;
  stocktake_number: string;
}> {
  const { data, error } = await supabase.rpc('create_stocktake', {
    p_title: payload.title.trim(),
    p_item_type: payload.item_type || 'all',
    p_notes: payload.notes?.trim() || null,
  });

  if (error) throw error;
  return data as any;
}

export async function updateStocktakeCountRpc(payload: {
  stocktake_id: string;
  item_id: string;
  counted_quantity: number;
  notes?: string | null;
}): Promise<{ success: boolean }> {
  const { data, error } = await supabase.rpc('update_stocktake_count', {
    p_stocktake_id: payload.stocktake_id,
    p_item_id: payload.item_id,
    p_counted_quantity: payload.counted_quantity,
    p_notes: payload.notes?.trim() || null,
  });

  if (error) throw error;
  return data as any;
}

export async function finalizeStocktakeRpc(payload: {
  stocktake_id: string;
  reason: string;
}): Promise<{
  success: boolean;
  stocktake_id: string;
  total_lines_counted: number;
  matching_lines: number;
  shortage_lines: number;
  overage_lines: number;
  total_cost_variance: number;
}> {
  const { data, error } = await supabase.rpc('finalize_stocktake', {
    p_stocktake_id: payload.stocktake_id,
    p_reason: payload.reason.trim(),
  });

  if (error) throw error;
  return data as any;
}
